import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { useGetMeQuery } from "@/features/auth/api";
import type { UserLoginContext } from "@/lib/authScope";
import { setLoginContext } from "@/store/slices/authSlice";

/** How often to re-read the user row while a tab sits open. */
const POLL_MS = 60_000;

export interface LoginContextState {
  context?: UserLoginContext;
  /**
   * Whether the backend has answered at all. Until it has, the shell renders
   * no navigation rather than guessing — showing every entry and then taking
   * them away is worse than a brief empty rail. A *failed* call also counts:
   * a session minted before this contract existed must not be stranded.
   */
  resolved: boolean;
}

/**
 * Hydrate — and keep hydrating — the backend authorisation context.
 *
 * Access tokens outlive a deactivation: they are valid for 24h and no
 * endpoint other than `/auth/me` re-reads the user row, so nothing else in
 * the app would ever notice. Polling here is what evicts a switched-off
 * account from an open tab within about a minute; the 401 that comes back is
 * turned into a proper sign-out by the axios interceptor.
 */
export function useLoginContext(enabled: boolean): LoginContextState {
  const dispatch = useDispatch();
  const pathname = useLocation().pathname;

  const { data, isSuccess, isError, refetch } = useGetMeQuery(undefined, {
    skip: !enabled,
    pollingInterval: POLL_MS,
  });

  useEffect(() => {
    if (!data) return;
    dispatch(
      setLoginContext({
        // The bare username and the account id: the session only knows the
        // token subject ("Tonya-BEREI"), which is the wrong value to send to
        // reset-password and cannot be matched against a row in the user list.
        userId: data.userId,
        username: data.username,
        requirePasswordChange: data.requirePasswordChange,
        features: data.features,
        writableFeatures: data.writableFeatures,
        visibleMenuIds: data.visibleMenuIds,
        manageableLevels: data.manageableLevels,
        assignableUserLevels: data.assignableUserLevels,
        commissionSections: data.commissionSections,
        agentMappings: data.agentMappings,
        isAgent: data.isAgent,
        scope: data.scope,
      })
    );
  }, [data, dispatch]);

  // Re-check on navigation as well as on the timer, so a user switched off
  // mid-session is stopped at the next page rather than up to a minute later.
  useEffect(() => {
    if (enabled) void refetch();
  }, [pathname, enabled, refetch]);

  return { context: data, resolved: isSuccess || isError };
}
